import React, { FunctionComponent } from "react";
import styles from "./TextVideo.module.scss";
import TextVideoBlockProps from "./TextVideoProps.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getVideoAspectRatio from "app/media/helpers/getVideoAspectRatio";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import Paragraph from "components/Paragraph/Paragraph";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useAnchor from "blocks/hooks/useAnchor";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import VideoEmbed from "components/VideoEmbed/VideoEmbed";
import VideoViewer from "components/VideoViewer/VideoViewer";
import IconViewer from "components/IconViewer/IconViewer";
import urlBuilder from "app/umbraco/helpers/urlBuilder";
import LazyLoad from "react-lazyload";

const TextVideo: FunctionComponent<TextVideoBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;

    const video = props.content?.video?.[0] && props.content?.video.length > 0 ? props.content.video[0].content : undefined;
    const videoAspectRatio = getVideoAspectRatio(video?.videoCrop, "Widescreen (16:9)");
    const videoPositionClass = props.settings.videoPosition === "Media Right" ? ` ${styles.has_video_right}` : undefined;
    const isAutoplay = video && video.videoAutoplay ? video.videoAutoplay : false;
    const isLoop = video && video.videoLoop ? video.videoLoop : false;
    const showControls = !isAutoplay;

    const imageUrl = props.content.image ? urlBuilder(props.content.image.umbracoFile.src).toString() : "";

    return (
        <section
            className={combineClasses(styles.wrapper, ...wrapperClasses)}
            style={{ ...wrapperStyles, ...splitStyles }}
            id={identifier}
        >
            <div
                className={combineClasses(styles.container, ...splitClasses, videoPositionClass, inViewClasses)}
                ref={inViewElementRef}
            >
                <div className={styles.media}>
                    {/* Local Video */}
                    {video && video._contentType === "blockVideoNestedLocal" && video.video?.umbracoFile ? (
                        <VideoViewer
                            src={video.video?.umbracoFile}
                            poster={video?.poster?.umbracoFile?.src}
                            alt={props.content.videoAlternative}
                            autoplay={isAutoplay}
                            loop={isLoop}
                            controls={showControls}
                            aspectRatioX={videoAspectRatio.aspectRatioX}
                            aspectRatioY={videoAspectRatio.aspectRatioY}
                        />
                    ) : null}

                    {/* Video Embed */}
                    {video && video._contentType === "blockVideoNestedYouTube" && video.videoUrl ? (
                        <VideoEmbed
                            src={video.videoUrl}
                            alt={props.content.videoAlternative}
                            autoplay={isAutoplay}
                            loop={isLoop}
                            aspectRatioX={16}
                            aspectRatioY={9}
                        />
                    ) : null}

                    {/* Caption */}
                    {props.content.videoCaption && props.content.videoCaption.length > 0 ? (
                        <div className={styles.caption}>{props.content.videoCaption}</div>
                    ) : null}

                    {/* Spinning text - image */}
                    {props.content.icon ? (
                        <div className={styles.spinner_wrapper}>
                            <div className={styles.icon_wrapper}>
                                <IconViewer
                                    className={styles.icon}
                                    name={props.content.icon.toLowerCase()}
                                />

                                {props.content.image ? (
                                    <LazyLoad>
                                        <img
                                            src={imageUrl}
                                            className={styles.image}
                                        />
                                    </LazyLoad>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className={combineClasses(styles.content)}>
                    {/* Tagline */}
                    {props.content.tagline ? <span className={styles.tagline}>{props.content.tagline}</span> : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading
                            className={combineClasses(styles.headline, headlineCaseClass)}
                            text={props.content.headline}
                            headlineSize={props.settings.headlineSize}
                            headlineWeight={props.settings.headlineWeight}
                        />
                    ) : null}

                    {/* Text */}
                    {props.content.bodyText ? (
                        <Paragraph
                            className={styles.text}
                            innerHTML={props.content.bodyText}
                        />
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button, index) => {
                                return (
                                    <Button
                                        key={index}
                                        to={button.content.link}
                                        className={styles.button}
                                        variant={button.content.variant}
                                        {...button.content}
                                    >
                                        {button.content.label}
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia
                    key={props.contentUdi}
                    {...props.settings.backgroundMedia[0]}
                />
            ) : null}
        </section>
    );
};

export default TextVideo;
